import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKeys } from '../queries/querykeys'
import businessApi from '../api/business.api'
import { UserAuth } from '../context/AuthContext'

interface MovementFilters {
  code?: string
  searchQuery?: string
  documentStatus?: string,
  type: string,
  accountId?: number,
  minAmount?: number,
  maxAmount?: number,
  folio?: string,
  nombreCuenta?: string,
  centroCosto?: string
}

interface UseInfiniteMovementsParams {
  businessId: number
  year?: number
  month?: number
  linkage: boolean
  limit: number
  page?: number
  filters: MovementFilters
  aprobadoMin?: number
  enabled?: boolean
}

const getMovementsResponse = async (params: UseInfiniteMovementsParams) => {
  const { businessId, year, month, linkage, limit, page, filters, aprobadoMin } = params
  return businessApi.getListSummaryMovementsSpecific({
    businessId,
    year,
    month,
    linkage,
    max: limit,
    page,
    ...filters,
    aprobadoMin
  })
}

export const useInfiniteMovements = ({
  businessId,
  year,
  month,
  linkage,
  limit,
  filters,
  aprobadoMin,
  enabled = true
}: UseInfiniteMovementsParams) => {
  const { isAuthenticated } = UserAuth()

  return useInfiniteQuery({
    enabled: enabled && isAuthenticated && !!businessId,
    queryKey: [
      QueryKeys.SUMMARY_MOVEMENTS_FROM_TO,
      businessId,
      year,
      month,
      linkage,
      limit,
      filters,
      aprobadoMin
    ],
    gcTime: 10 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ pageParam = 0}) => {
      const movementsResponse = await getMovementsResponse({
        businessId,
        year,
        month,
        linkage,
        limit,
        page: pageParam,
        filters,
        aprobadoMin
      })
      const movementsData = movementsResponse.data
      if (!movementsData || movementsData.length === 0) {
        return {
          data: [],
          totalPages: 0,
          currentPage: 0,
          hasNextPage: false,
          count: 0
        }
      }
      const { libros, count } = movementsData
      const totalPages = Math.ceil(count / limit)
      const hasNextPage = libros.length >= limit
      return {
        data: libros,
        totalPages: totalPages,
        currentPage: pageParam,
        hasNextPage,
        count
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined
    },
    initialPageParam: 0
  })
}
